<!--
@component
Commentary

See markup below.
-->

<script lang="ts">
  import Default from './cards/Default.svelte';
  import Special from './cards/Special.svelte';
  import Placeholder from './cards/Placeholder.svelte';
  import LazyMount from '$lib/components/common/LazyMount/index.svelte';
  import type { CommentaryEntry } from '$lib/@types';
  import type { SpecialIcon, SpecialType } from './cards/types';

  /** Reserved height for off-screen commentary cards (rough average). */
  const COMMENT_CARD_HEIGHT = '8rem';

  type SpecialCardConfig = {
    title: string;
    icon: SpecialIcon;
    type?: SpecialType;
  };

  // `commentary` is the array delivered by the Opta live-commentary feed.
  // Typed loosely as an array of `CommentaryEntry` so upstream callers that
  // pass data through as `unknown` continue to compile.
  let {
    commentary = [],
    isLive,
  }: { commentary?: CommentaryEntry[]; isLive?: boolean } = $props();

  // Map each commentary `type` value to the props passed to <Special />.
  // Types not present in this map fall through to <Default />.
  // Types commented out below are intentionally rendered as Default for now:
  // 'pre kick off', 'kick off', 'assist', 'penalty miss', 'penalty save',
  // 'penalty_so goal', 'penalty_so miss'
  const SPECIAL_CARDS: Record<string, SpecialCardConfig> = {
    goal: { title: 'Goal!', icon: 'football', type: 'goal' },
    'own goal': { title: 'Goal!', icon: 'football', type: 'goal' },
    'penalty goal': { title: 'Goal!', icon: 'football', type: 'goal' },
    substitution: {
      title: 'Substitution',
      icon: 'substitution',
      type: 'substitution',
    },
    'second_yellow red card': {
      title: 'Red card',
      icon: 'second-yellow-card',
      type: 'redCard',
    },
    'secondyellow card': {
      title: 'Red card',
      icon: 'second-yellow-card',
      type: 'redCard',
    },
    'red card': { title: 'Red card', icon: 'red-card', type: 'redCard' },
    'yellow card': {
      title: 'Yellow card',
      icon: 'yellow-card',
      type: 'yellowCard',
    },
    stats: { title: 'Stats', icon: 'stats' },
    highlight: { title: 'Highlight', icon: 'highlight', type: 'highlight' },
    'video referee': { title: '', icon: 'var', type: 'var' },
    'half time': { title: 'Half Time', icon: 'whistle', type: 'ht-ft' },
    'full time': { title: 'Full Time', icon: 'whistle', type: 'ht-ft' },
  };

  let showHighlights = $state(false);

  const commentList = $derived(
    showHighlights ?
      commentary.filter((comment) => comment.type in SPECIAL_CARDS)
    : commentary
  );

  const toggleHighlights = (event: Event) => {
    event.preventDefault();
    showHighlights = !showHighlights;
  };
</script>

<!-- <h2 class="title text-center fmb-5">
  Commentary {showHighlights ? 'highlights' : ''}
</h2> -->

<!-- <div class="highlights-btn">
  <button
    onclick={toggleHighlights}
    class:showHighlights
    type="button"
    class="btn"
    >{showHighlights ? 'Show all the action' : 'Show highlights'}</button
  >
</div> -->

<!--
  Commentary feeds can run 100+ entries on a full match. Each entry is
  wrapped in `LazyMount` so off-screen cards (with their icons, animations,
  and per-entry styling) don't hydrate or paint until the user scrolls them
  near the viewport. See #57.
-->
<!--
  Placeholder rendered inside `LazyMount`'s reserved space until each
  commentary card scrolls near the viewport. Mirrors the Default card's
  layout so swapping in the live card doesn't shift surrounding cards'
  positions.
-->
{#snippet placeholder()}
  <Placeholder label={null} />
{/snippet}

{#each commentList as comment, i (comment.id)}
  {@const special = SPECIAL_CARDS[comment.type]}
  <LazyMount height={COMMENT_CARD_HEIGHT} {placeholder}>
    {#if special}
      <Special
        {comment}
        {i}
        title={special.title}
        icon={special.icon}
        type={special.type}
      />
    {:else}
      <Default {isLive} {comment} {i} />
    {/if}
  </LazyMount>
{/each}

<style lang="scss">
  @use '../../../styles/mixins' as mixins;

  a {
    text-decoration: inherit;
    color: inherit;
  }

  .highlights-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    width: 100%;

    .btn {
      margin: auto;
      width: 14rem;
      @include mixins.button-primary;
    }
  }
</style>
